import SvgIcon, { type SvgIconProps } from "@mui/material/SvgIcon"
import * as React from "react"

export function MaterialIcon({
  icon,
  ...props
}: { icon: string } & SvgIconProps) {
  const IconComponent = icons.get(icon)

  React.useEffect(() => {
    if (!IconComponent) {
      console.error(
        `No Material icon found for identifier "${icon}". Check in \`packages/website/src/js/components/MaterialIcon.tsx\` if this icon is registered.`,
      )
    }
  }, [IconComponent, icon])

  if (!IconComponent) {
    return null
  }

  return (
    <React.Suspense fallback={null}>
      <IconComponent {...props} />
    </React.Suspense>
  )
}

const iconLoaders = {
  Dashboard: () => import("@mui/icons-material/Dashboard"),
  Task: () => import("@mui/icons-material/Task"),
  AccountCircle: () => import("@mui/icons-material/AccountCircle"),
  Inventory: () => import("@mui/icons-material/Inventory"),
  PrecisionManufacturing: () =>
    import("@mui/icons-material/PrecisionManufacturing"),
  InstallMobile: () => import("@mui/icons-material/InstallMobile"),
  Outbound: () => import("@mui/icons-material/Outbound"),
  Business: () => import("@mui/icons-material/Business"),
  Support: () => import("@mui/icons-material/Support"),
  RequestQuote: () => import("@mui/icons-material/RequestQuote"),
  GppMaybe: () => import("@mui/icons-material/GppMaybe"),
  Assignment: () => import("@mui/icons-material/Assignment"),
  AssignmentInd: () => import("@mui/icons-material/AssignmentInd"),
  AssignmentTurnedIn: () => import("@mui/icons-material/AssignmentTurnedIn"),
  AssignmentReturn: () => import("@mui/icons-material/AssignmentReturn"),
  AssignmentLate: () => import("@mui/icons-material/AssignmentLate"),
  AssignmentReturned: () => import("@mui/icons-material/AssignmentReturned"),
  Message: () => import("@mui/icons-material/Message"),
  FormatListNumbered: () => import("@mui/icons-material/FormatListNumbered"),
  CallSplit: () => import("@mui/icons-material/CallSplit"),
  CheckCircle: () => import("@mui/icons-material/CheckCircle"),
  LocalShipping: () => import("@mui/icons-material/LocalShipping"),
  ShoppingCartCheckout: () =>
    import("@mui/icons-material/ShoppingCartCheckout"),
  FactCheck: () => import("@mui/icons-material/FactCheck"),
  RvHookup: () => import("@mui/icons-material/RvHookup"),
  Article: () => import("@mui/icons-material/Article"),
  ChangeCircle: () => import("@mui/icons-material/ChangeCircle"),
  BuildCircle: () => import("@mui/icons-material/BuildCircle"),
  Place: () => import("@mui/icons-material/Place"),
  HouseSiding: () => import("@mui/icons-material/HouseSiding"),
  Grass: () => import("@mui/icons-material/Grass"),
  Work: () => import("@mui/icons-material/Work"),
  ShoppingCart: () => import("@mui/icons-material/ShoppingCart"),
  Sell: () => import("@mui/icons-material/Sell"),
  Warehouse: () => import("@mui/icons-material/Warehouse"),
  Language: () => import("@mui/icons-material/Language"),
  BusinessCenter: () => import("@mui/icons-material/BusinessCenter"),
  HelpCenter: () => import("@mui/icons-material/HelpCenter"),
  TableChart: () => import("@mui/icons-material/TableChart"),
  History: () => import("@mui/icons-material/History"),
  LocationCity: () => import("@mui/icons-material/LocationCity"),
  PlaylistAddCheck: () => import("@mui/icons-material/PlaylistAddCheck"),
  Group: () => import("@mui/icons-material/Group"),
  NoteAlt: () => import("@mui/icons-material/NoteAlt"),
  Receipt: () => import("@mui/icons-material/Receipt"),
  AccountTree: () => import("@mui/icons-material/AccountTree"),
  EventNote: () => import("@mui/icons-material/EventNote"),
  Security: () => import("@mui/icons-material/Security"),
  DateRange: () => import("@mui/icons-material/DateRange"),
  Engineering: () => import("@mui/icons-material/Engineering"),
  People: () => import("@mui/icons-material/People"),
  Schedule: () => import("@mui/icons-material/Schedule"),
  WbSunny: () => import("@mui/icons-material/WbSunny"),
  Api: () => import("@mui/icons-material/Api"),
  Assessment: () => import("@mui/icons-material/Assessment"),
  Shield: () => import("@mui/icons-material/Shield"),
  AdminPanelSettings: () => import("@mui/icons-material/AdminPanelSettings"),
  FolderShared: () => import("@mui/icons-material/FolderShared"),
  CreateNewFolder: () => import("@mui/icons-material/CreateNewFolder"),
  SnippetFolder: () => import("@mui/icons-material/SnippetFolder"),
  RuleFolder: () => import("@mui/icons-material/RuleFolder"),
  FolderSpecial: () => import("@mui/icons-material/FolderSpecial"),
  SportsSoccer: () => import("@mui/icons-material/SportsSoccer"),
  Factory: () => import("@mui/icons-material/Factory"),
  AddShoppingCart: () => import("@mui/icons-material/AddShoppingCart"),
  ProductionQuantityLimits: () =>
    import("@mui/icons-material/ProductionQuantityLimits"),
  BarChart: () => import("@mui/icons-material/BarChart"),
  Grain: () => import("@mui/icons-material/Grain"),
  Home: () => import("@mui/icons-material/Home"),
  PeopleAlt: () => import("@mui/icons-material/PeopleAlt"),
} satisfies Record<string, () => Promise<{ default: typeof SvgIcon }>>

const icons = new Map(
  Object.entries(iconLoaders).map(([identifier, load]) => [
    identifier,
    React.lazy(load),
  ]),
)
